import { osloDateString } from "@/lib/oslo-time";

/** En rad fra lead_attempt_stats_v: tall per møtebooker og dag (Oslo-dato). */
export interface AttemptStatsRow {
  user_id: string;
  day: string;
  calls: number;
  answered: number;
  meetings_booked: number;
  meetings_held: number;
}

export interface ActivityTotals {
  calls: number;
  answered: number;
  meetings_booked: number;
  meetings_held: number;
}

export const EMPTY_TOTALS: ActivityTotals = { calls: 0, answered: 0, meetings_booked: 0, meetings_held: 0 };

/** Svarandel (0 til 1), eller null uten samtaler. */
export function answerRate(t: Pick<ActivityTotals, "calls" | "answered">): number | null {
  return t.calls > 0 ? t.answered / t.calls : null;
}

/** Møter booket per samtale (0 til 1), eller null uten samtaler. */
export function meetingsPerCall(t: Pick<ActivityTotals, "calls" | "meetings_booked">): number | null {
  return t.calls > 0 ? t.meetings_booked / t.calls : null;
}

export function addTotals(a: ActivityTotals, row: Partial<ActivityTotals>): ActivityTotals {
  return {
    calls: a.calls + (row.calls ?? 0),
    answered: a.answered + (row.answered ?? 0),
    meetings_booked: a.meetings_booked + (row.meetings_booked ?? 0),
    meetings_held: a.meetings_held + (row.meetings_held ?? 0),
  };
}

/** Mandag i uken (Oslo-dato, YYYY-MM-DD) som datoen ligger i. */
export function weekStart(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00Z`);
  const offset = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
}

export const inRange = (day: string, from: string, to: string): boolean => day >= from && day <= to;

export interface UserActivity {
  user_id: string;
  today: ActivityTotals;
  week: ActivityTotals;
  period: ActivityTotals;
}

/**
 * Tall per møtebooker for i dag, denne uken (fra mandag) og den valgte perioden. Hele dagen i dag
 * og uken regnes i Oslo-tid fra `now`. Brukere uten aktivitet i noen av dem er ikke med.
 */
export function summarizeByUser(rows: readonly AttemptStatsRow[], now: Date, period: { fromDate: string; toDate: string }): UserActivity[] {
  const today = osloDateString(now);
  const week = weekStart(today);
  const map = new Map<string, UserActivity>();
  for (const r of rows) {
    const entry = map.get(r.user_id) ?? { user_id: r.user_id, today: EMPTY_TOTALS, week: EMPTY_TOTALS, period: EMPTY_TOTALS };
    if (r.day === today) entry.today = addTotals(entry.today, r);
    if (inRange(r.day, week, today)) entry.week = addTotals(entry.week, r);
    if (inRange(r.day, period.fromDate, period.toDate)) entry.period = addTotals(entry.period, r);
    map.set(r.user_id, entry);
  }
  return [...map.values()].filter((u) => u.today.calls + u.week.calls + u.period.calls > 0);
}

/** Aggregering per uke (nyeste først), til trend over perioden. */
export function weeklyTotals(rows: readonly AttemptStatsRow[]): { week: string; totals: ActivityTotals }[] {
  const map = new Map<string, ActivityTotals>();
  for (const r of rows) {
    const w = weekStart(r.day);
    map.set(w, addTotals(map.get(w) ?? EMPTY_TOTALS, r));
  }
  return [...map.entries()].map(([week, totals]) => ({ week, totals })).sort((a, b) => b.week.localeCompare(a.week));
}

export interface ListYieldInput {
  total: number;
  called_count: number;
  in_pipe_count: number;
  customer_count: number;
}

/** Ringt-andel av listen og utbytte (leads som ble prosess eller kunde) av dem som er ringt. null uten grunnlag. */
export function listYield(s: ListYieldInput): { calledShare: number | null; yieldShare: number | null } {
  return {
    calledShare: s.total > 0 ? s.called_count / s.total : null,
    yieldShare: s.called_count > 0 ? (s.in_pipe_count + s.customer_count) / s.called_count : null,
  };
}

/** Datointervallet hooken må hente: fra tidligste av periodens start og ukens mandag til seneste av periodens slutt og i dag. */
export function fetchRange(now: Date, period: { fromDate: string; toDate: string }): { from: string; to: string } {
  const today = osloDateString(now);
  const week = weekStart(today);
  return { from: period.fromDate < week ? period.fromDate : week, to: period.toDate > today ? period.toDate : today };
}
